import { convertColorStringToRgb } from "./wcag.convert-color-to-rgb";
import type { ContrastRatioOptions } from "./wcag.verify-contrast-ratio";
import { verifyWCAGContrastRatio } from "./wcag.verify-contrast-ratio";

type Interaction = "hover" | "active";

const shadingMap = {
  darken: {
    hover: -0.35,
    active: -0.45,
  },
  lighten: {
    hover: -0.1,
    active: -0.15,
  },
};

/**
 * relatively simple algorithm to apply shading to a color to allow user
 * feedback for different interaction states automatically for arbitrary colors
 *
 * @see https://stackoverflow.com/a/13542669
 */
export function shadeColorForInteraction(
  color: string,
  interaction: Interaction,
  options?: ContrastRatioOptions,
): `${number} ${number} ${number}` {
  const rgbColor = convertColorStringToRgb(color);

  // if dark text would be allowed on top of the color, then we will darken it
  // when applying shading (so as to increase target contrast ratio in order to
  // ensure that we don't accidentally create interaction states with contrast
  // ratios that doesn't meet the expected criteria)
  const mode = verifyWCAGContrastRatio(color, "#fff", options)
    ? "darken"
    : "lighten";

  // now determine how to shade the color for the desired interaction
  const shade = shadingMap[mode][interaction];

  // ... and apply shading based on the algorithm from Stack overflow
  // (simplified because we've already done extraction of the rgb color segments
  // and want to return them "as is")
  const t = shade < 0 ? 0 : shade * 255 ** 2;
  const p = shade < 0 ? 1 + shade : 1 - shade;

  const applyShade = (input: number | undefined) =>
    Math.round((p * (input ?? 0) ** 2 + t) ** 0.5);

  return `${applyShade(rgbColor?.r)} ${applyShade(rgbColor?.g)} ${applyShade(
    rgbColor?.b,
  )}`;
}
